// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Fab,
  Fade,
  IconButton,
  Paper,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { RootState, useAppSelector } from "../../slices/store";
import { contactDetails, LOCAL_STORAGE_KEYS, BANNER_TOGGLE_EVENT } from "../../config/constant";
import { BannerToggleDetail } from "../../utils/types";

type WelcomeCardProps = {
  initialOpen?: boolean;
};

const WelcomeCard = ({ initialOpen }: WelcomeCardProps) => {
  const contactState = useAppSelector((state: RootState) => state.contact);

  const [open, setOpen] = useState<boolean>(() => {
    if (initialOpen !== undefined) {
      return initialOpen;
    }
    if (typeof window !== "undefined") {
      return localStorage.getItem(LOCAL_STORAGE_KEYS.WELCOME_DIALOG_DISMISSED) !== "hide";
    }
    return true;
  });

  // Listen for banner toggle events
  useEffect(() => {
    const onBannerToggle = (e: Event) => {
      const ev = e as CustomEvent<BannerToggleDetail>;
      if (ev?.detail?.welcome !== undefined) {
        setOpen(!!ev.detail.welcome);
      }
    };

    window.addEventListener(BANNER_TOGGLE_EVENT, onBannerToggle as EventListener);
    return () =>
      window.removeEventListener(BANNER_TOGGLE_EVENT, onBannerToggle as EventListener);
  }, []);

  return (
    <>
      <Slide timeout={1000} direction="up" in={open} mountOnEnter unmountOnExit>
        <Card
          sx={{
            width: 250,
            minHeight: 280,
            position: "fixed",
            bottom: 80,
            right: 20,
            zIndex: "1000",
            borderRadius: 5,
            py: 3,
            px: 1,
          }}
          elevation={3}
        >
          <Stack
            sx={{ mr: 1, mt: -2 }}
            direction="row"
            justifyContent="flex-end"
          >
            <IconButton
              onClick={() => {
                setOpen(false);
                localStorage.setItem(LOCAL_STORAGE_KEYS.WELCOME_DIALOG_DISMISSED, "hide");
                window.dispatchEvent(
                  new CustomEvent<BannerToggleDetail>(BANNER_TOGGLE_EVENT, { detail: { welcome: false } })
                );
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack direction="row" justifyContent="center" mt={-3}>
            <img src="/welcome.svg" alt="welcome" height={80} />
          </Stack>
          <CardContent sx={{ marginTop: -2 }}>
            <Typography
              sx={{ fontWeight: "bold" }}
              variant="h5"
              color="secondary.main"
            >
              Hello,
            </Typography>
            <Typography variant="h5" color="secondary.main">
              {contactState.response.firstName}
            </Typography>

            <Typography variant="body2" mt={2}>
              Welcome to the new WSO2 Certification Portal.
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={2}>
              Explore it and share your thoughts and feedback with us.
            </Typography>
          </CardContent>
          <Stack direction="row" justifyContent="center">
            <Button
              size="small"
              sx={{ px: 2, borderRadius: 10, fontSize: 12 }}
              variant="contained"
              color="secondary"
              href={contactDetails.mailTo}
            >
              Share Feedback
            </Button>
          </Stack>
        </Card>
      </Slide>
      {/* FAB rendering moved to LandingPage to avoid overlap with other banners */}
    </>
  );
};

export default WelcomeCard;
